import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorDetails, errorMessage } from '../lib/api';
import { countKeyedAnswers } from '../lib/answer-key';
import type { Diagnostic, ImportOutcome } from '../lib/tests';
import { WritingPaperForm } from './WritingPaperForm';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { DiagnosticList } from '../components/DiagnosticList';

interface PassagePreview {
  part: number;
  title: string | null;
  paragraphs: number;
  html: string;
}

interface ReadSummary {
  part: number;
  range: string;
  type: string;
  /** The type in words. Older responses carry only the stored name. */
  label?: string;
  maxWords: string | null;
}

interface OcrPage {
  page: number;
  text: string;
  confidence: number;
}

type Module = 'LISTENING' | 'READING' | 'WRITING';

/**
 * Adding a paper, one decision at a time.
 *
 * This screen used to open on an empty monospace box with seven buttons round
 * it — start a listening paper, convert it, see an example, read a scan — and
 * a teacher had to work out which of them applied to what they were holding.
 * The work itself is three questions: which paper is this, what does it say,
 * and what does it come with. So it asks them in that order, and shows nothing
 * about a step that is not the current one.
 *
 * The template is still underneath, and a paper can still be written in it
 * directly, but nobody has to know that to add a paper any more.
 */
export function WritePaperPage() {
  const navigate = useNavigate();
  /**
   * Arriving from a gap in the whole-tests table.
   *
   * "Test 1 · Reading · not added" is a sentence about work to do, and the
   * only way to do it was to come here and say both of those things again
   * from memory. Following the link brings them with it.
   */
  const [params] = useSearchParams();
  const wantedModule = params.get('module');
  const wantedSet = params.get('set');

  const [module, setModule] = useState<Module | null>(
    wantedModule === 'LISTENING' || wantedModule === 'READING' || wantedModule === 'WRITING'
      ? (wantedModule as Module)
      : null,
  );
  /**
   * The whole test this paper belongs to. Three papers sharing a name are one
   * test, so the field defaults to the last one used: a teacher adding the
   * Reading after the Listening means the same test almost every time.
   */
  const [setName, setSetName] = useState(wantedSet ?? '');
  const [knownSets, setKnownSets] = useState<string[]>([]);
  const [template, setTemplate] = useState('');
  /**
   * The answer key, in its own box.
   *
   * A teacher copying from a book has the paper on one page and the key on
   * another — often in a different file. Asking for them in one box means
   * editing the paper to make room for a section the importer strips out
   * again, and the editing is where a key gets truncated.
   */
  const [answerKey, setAnswerKey] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<Diagnostic[] | null>(null);
  const [pages, setPages] = useState<OcrPage[] | null>(null);
  const [summary, setSummary] = useState<ReadSummary[] | null>(null);
  const [passages, setPassages] = useState<PassagePreview[] | null>(null);
  const [converting, setConverting] = useState(false);

  /**
   * The recordings a Listening paper is sat to, and the pictures its labelling
   * questions need.
   *
   * A list, not one file: a paper's audio arrives as one continuous recording
   * or as one per part, and centres hold it both ways. Uploaded in the order
   * they are added, which is the order the parts are sat.
   */
  const [recordings, setRecordings] = useState<File[]>([]);
  const [pictures, setPictures] = useState<File[]>([]);

  const scanInput = useRef<HTMLInputElement | null>(null);
  const audioInput = useRef<HTMLInputElement | null>(null);
  const pictureInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api
      .get<{ tests: { setName: string | null }[] }>('/tests')
      .then(({ data }) => {
        const names = [...new Set(data.tests.map((t) => t.setName).filter(Boolean))] as string[];
        setKnownSets(names);
        setSetName((current) => current || names[0] || 'Test 1');
      })
      .catch(() => setSetName((current) => current || 'Test 1'));
  }, []);

  async function readScan(files: FileList) {
    setReading(true);
    setError(null);
    try {
      const form = new FormData();
      for (const file of Array.from(files)) form.append('pages', file);
      const { data } = await api.post<{ pages: OcrPage[] }>('/tests/ocr', form);
      setPages(data.pages);
    } catch (err) {
      setError(errorMessage(err, 'Those pages could not be read'));
    } finally {
      setReading(false);
    }
  }

  /**
   * Paper-shaped text → the template, in place.
   *
   * Not a step any more — Import does this on its own — but still here for a
   * paper worth checking line by line before it becomes one.
   */
  /**
   * How many answers the key box holds, counted the way the importer counts
   * them, so "38 answers" beside a 40-question paper is a cue to look for the
   * two that were missed rather than a disagreement with what happens next.
   */
  const keyedAnswers = useMemo(() => countKeyedAnswers(answerKey), [answerKey]);

  async function convertPaper() {
    setConverting(true);
    setError(null);
    setRejected(null);
    try {
      const { data } = await api.post<{
        template: string;
        diagnostics: Diagnostic[];
        summary: ReadSummary[];
      }>('/tests/paper-text', {
        paper: template,
        answerKey: answerKey.trim() || undefined,
        title: title.trim() || undefined,
        module: module ?? undefined,
      });
      setTemplate(data.template);
      setSummary(data.summary);
      if (data.diagnostics.length > 0) setRejected(data.diagnostics);

      /**
       * And what the passages will look like.
       *
       * Asked for separately so a failure here cannot stop a paper being read:
       * the preview is a reassurance, not a step. It runs the same parser the
       * import runs, so what it shows is what will be stored.
       */
      try {
        const shown = await api.post<{ passages: PassagePreview[] }>('/tests/preview', {
          paper: template,
          answerKey: answerKey.trim() || undefined,
          title: title.trim() || undefined,
          module: module ?? undefined,
        });
        setPassages(shown.data.passages);
      } catch {
        setPassages(null);
      }
    } catch (err) {
      setError(errorMessage(err, 'That could not be read as a paper'));
    } finally {
      setConverting(false);
    }
  }

  async function importIt() {
    setBusy(true);
    setError(null);
    setRejected(null);
    try {
      const { data } = await api.post<ImportOutcome>('/tests/import/text', {
        template,
        answerKey: answerKey.trim() || undefined,
        title: title.trim() || undefined,
        module: module ?? undefined,
        setName: setName.trim() || undefined,
      });

      // The paper exists from here on. A file that will not attach is worth
      // saying so about, but it is not a reason to throw the import away.
      const attached: string[] = [];
      for (const file of [...recordings, ...pictures]) {
        try {
          const form = new FormData();
          form.append('file', file);
          const { data: upload } = await api.post<{ note: string | null }>(
            `/tests/${data.testId}/assets`,
            form,
          );
          attached.push(upload.note ?? `"${file.name}" was attached.`);
        } catch (err) {
          attached.push(errorMessage(err, `"${file.name}" could not be attached`));
        }
      }

      navigate(`/tests/${data.testId}`, { state: { attached } });
    } catch (err) {
      setError(errorMessage(err, 'That could not be imported'));
      setRejected(errorDetails<{ diagnostics?: Diagnostic[] }>(err)?.diagnostics ?? null);
    } finally {
      setBusy(false);
    }
  }

  /* Writing is asked for as two prompts rather than as pasted text: it is the
     one module a form can ask for completely. */
  if (module === 'WRITING') {
    return (
      <>
        <PageHeader
          title="Add a Writing paper"
          meta={
            <p className="muted">
              Two tasks, and the chart or diagram Task 1 describes.
              {setName.trim() && <> Part of {setName.trim()}.</>}
            </p>
          }
          actions={
            <button className="btn" onClick={() => setModule(null)}>
              ← Choose another module
            </button>
          }
        />
        <WritingPaperForm onCancel={() => setModule(null)} setName={setName} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Add a paper"
        meta={
          <p className="muted" style={{ maxWidth: '64ch' }}>
            {module
              ? 'Paste the paper as it is printed. Nothing is published until you have seen what was made of it.'
              : 'Three questions: which paper this is, what it says, and what it comes with.'}
          </p>
        }
        actions={
          module ? (
            <button
              className="btn btn--primary"
              disabled={busy || template.trim().length < 20}
              onClick={() => void importIt()}
            >
              {busy ? 'Reading it…' : 'Import'}
            </button>
          ) : null
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {rejected && rejected.length > 0 && (
          <div className="panel">
            <h2 className="panel__title">What the importer found</h2>
            <DiagnosticList diagnostics={rejected} />
          </div>
        )}

        {/* 1 — which paper this is. Everything below waits on it, because the
            answer changes what is asked for next. */}
        <section className="panel stack">
          <div>
            <h2 className="panel__title">1 · Which paper is this?</h2>
            <p className="muted">A paper is one module. Three of them make a full test.</p>
          </div>
          <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
            {(
              [
                ['LISTENING', 'Listening', '40 questions, one recording'],
                ['READING', 'Reading', '40 questions, three passages'],
                ['WRITING', 'Writing', 'Two tasks, marked by you'],
              ] as const
            ).map(([key, label, blurb]) => (
              <button
                key={key}
                className={`btn ${module === key ? 'btn--primary' : ''}`}
                onClick={() => setModule(key)}
              >
                {label}
                <span className="muted" style={{ marginLeft: '0.5rem', fontWeight: 400 }}>
                  {blurb}
                </span>
              </button>
            ))}
          </div>

          {/* The three modules of one test share a name, and that name is what
              a centre sets: "Test 1" rather than three papers in a row. */}
          <label className="field" style={{ maxWidth: '26rem' }}>
            <span className="field__label">Part of which test?</span>
            <input
              className="field__input"
              list="known-test-sets"
              placeholder="Test 1"
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
            />
            <span className="field__hint">
              Listening, Reading and Writing sharing this name make one whole test, which can then
              be set on a class in one go. Leave it empty for a paper that stands alone.
            </span>
          </label>
          <datalist id="known-test-sets">
            {knownSets.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </section>

        {module && (
          <>
            {/* 2 — the paper itself. */}
            <section className="panel stack">
              <div>
                <h2 className="panel__title">2 · Paste the paper</h2>
                <p className="muted" style={{ maxWidth: '64ch' }}>
                  Copy it as it is printed and paste it whole: the part headings ("PART 1 Questions
                  1–10"), the instructions in their own words, the questions with their numbers,
                  and the answer key under a heading that says so.
                </p>
                {/* Paragraphs are read off the shape of the paste and read off
                    it well, but a blank line is the one separator that is never
                    a guess — so it is worth saying out loud, once, here. */}
                <p className="muted" style={{ maxWidth: '64ch' }}>
                  A reading passage keeps its paragraphs. Leave a blank line between them if you
                  can; where there is none, the paragraphs are worked out from the paste, and you
                  can correct them on the next screen.
                </p>
              </div>

              <label className="field" style={{ maxWidth: '26rem' }}>
                <span className="field__label">Paper title</span>
                <input
                  className="field__input"
                  placeholder={module === 'LISTENING' ? 'Listening Test 1' : 'Reading Test 1'}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>

              <div className={pages ? 'writer writer--split' : 'writer'}>
                <textarea
                  className="writer__editor mono"
                  spellCheck={false}
                  placeholder={
                    module === 'LISTENING'
                      ? 'PART 1  Questions 1–10\n\nComplete the notes below.\n\nWrite ONE WORD AND/OR A NUMBER for each answer.\n\n…'
                      : 'READING PASSAGE 1\n\nThe life and work of Marie Curie\n\n…\n\nQuestions 1–6\n\nDo the following statements agree with the information given in Reading Passage 1?\n\n…'
                  }
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                />

                {pages && (
                  <div className="writer__scan">
                    <div className="writer__scanhead">
                      <strong>Read from your pages</strong>
                      <button className="btn btn--ghost" onClick={() => setPages(null)}>
                        Close
                      </button>
                    </div>
                    <p className="field__hint">
                      Character recognition, not a transcription. Check every word against the page,
                      and the answer keys especially — a misread key marks a correct answer wrong.
                    </p>
                    {pages.map((page) => (
                      <div key={page.page} className="writer__page">
                        <p className="eyebrow">
                          Page {page.page} · {page.confidence}% confident
                          {page.confidence < 75 && ' · read this one carefully'}
                        </p>
                        <pre className="writer__text">{page.text}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* The ways round the ordinary path, kept quiet: the scan only if
                  there is nothing to copy, the template only if somebody wants
                  to see it. */}
              <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
                <button
                  className="btn btn--ghost"
                  disabled={reading}
                  onClick={() => scanInput.current?.click()}
                >
                  {reading ? 'Reading the pages…' : 'Only have it on paper? Read a scan'}
                </button>
                <button
                  className="btn btn--ghost"
                  disabled={converting || template.trim().length < 40}
                  onClick={() => void convertPaper()}
                >
                  {converting ? 'Reading it…' : 'Show me the template it makes'}
                </button>
                <a
                  className="btn btn--ghost"
                  href="/api/v1/tests/template.txt"
                  target="_blank"
                  rel="noreferrer"
                >
                  See a full example
                </a>
                <input
                  ref={scanInput}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files?.length) void readScan(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>
            </section>

            {/* 3 — the key, on its own, because that is how a book prints it. */}
            {(
              <section className="panel stack">
                <div>
                  <h2 className="panel__title">3 · Paste the answer key</h2>
                  <p className="muted" style={{ maxWidth: '64ch' }}>
                    One line per question, as the book prints it — “1 Tuesday”, “1. Tuesday” and
                    “1-3 B” are all read. It is kept out of the paper the candidate reads, and
                    every answer can be corrected on the review screen afterwards.
                  </p>
                </div>

                <textarea
                  className="writer__editor mono"
                  spellCheck={false}
                  style={{ minHeight: '14rem' }}
                  aria-label="The answer key"
                  placeholder={'1 Tuesday\n2 £15\n3 Riverside\n4 half price\n5 7.30'}
                  value={answerKey}
                  onChange={(e) => setAnswerKey(e.target.value)}
                />

                <p className="field__hint">
                  {answerKey.trim()
                    ? `${keyedAnswers} answer${keyedAnswers === 1 ? '' : 's'} read from this box.`
                    : 'Optional here — a key pasted at the bottom of the paper above is still read, and answers can be filled in on the review screen.'}
                </p>
              </section>
            )}

            {/* 4 — everything the paper cannot be sat without. */}
            <section className="panel stack">
              <div>
                <h2 className="panel__title">4 · What it comes with</h2>
                <p className="muted" style={{ maxWidth: '64ch' }}>
                  Uploaded with the paper. Filenames do not have to match anything — a picture
                  fills the next questions that ask for one, in the order you add them.
                </p>
              </div>

              {module === 'LISTENING' && (
                <div className="stack" style={{ gap: 'var(--s-2)' }}>
                  <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
                    <button className="btn" onClick={() => audioInput.current?.click()}>
                      {recordings.length === 0
                        ? 'Add the recording'
                        : `Add another (${recordings.length} chosen)`}
                    </button>
                    <input
                      ref={audioInput}
                      type="file"
                      /* Endings as well as types: a picker that maps .m4a or
                         .opus to nothing greys out the very file being looked
                         for. */
                      accept="audio/*,.mp3,.m4a,.m4b,.aac,.wav,.ogg,.oga,.opus,.flac,.wma,.aiff,.mp4,.mov"
                      multiple
                      hidden
                      onChange={(e) => {
                        setRecordings((chosen) => [...chosen, ...Array.from(e.target.files ?? [])]);
                        e.target.value = '';
                      }}
                    />
                  </div>

                  {recordings.length > 0 && (
                    <ul className="checklist">
                      {recordings.map((file, index) => (
                        <li key={`${file.name}-${index}`}>
                          <span className="mono">
                            {recordings.length > 1 ? `Part ${index + 1}` : 'Whole test'}
                          </span>{' '}
                          {file.name}{' '}
                          <button
                            className="btn btn--ghost"
                            onClick={() =>
                              setRecordings((chosen) => chosen.filter((_, i) => i !== index))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="field__hint" style={{ maxWidth: '64ch' }}>
                    One file for the whole test, or one for each part — add them in the order the
                    parts are sat. Either can be changed on the review screen afterwards.
                  </p>
                </div>
              )}

              <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => pictureInput.current?.click()}>
                  {pictures.length === 0
                    ? 'Add a map or diagram'
                    : `Add another (${pictures.length} chosen)`}
                </button>
                <input
                  ref={pictureInput}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    setPictures((chosen) => [...chosen, ...Array.from(e.target.files ?? [])]);
                    e.target.value = '';
                  }}
                />
              </div>

              {pictures.length > 0 && (
                <ul className="checklist">
                  {pictures.map((file, index) => (
                    <li key={`${file.name}-${index}`}>
                      <span className="mono">{index + 1}.</span> {file.name}{' '}
                      <button
                        className="btn btn--ghost"
                        onClick={() =>
                          setPictures((chosen) => chosen.filter((_, i) => i !== index))
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {module === 'LISTENING' && recordings.length === 0 && (
                <p className="field__hint">
                  A Listening paper cannot be published without its recording. Add it here or on
                  the review screen afterwards.
                </p>
              )}
            </section>

            {/*
              * The passages, set the way the exam will set them.
              *
              * "Did my paragraphs survive?" is the question a paste raises,
              * and it used to be answerable only by importing the paper and
              * opening it. This answers it before anything exists, in the
              * exam's own type, from the same parser the import uses.
              */}
            {passages && passages.length > 0 && (
              <section className="panel stack">
                <div>
                  <h2 className="panel__title">Your passages, as a candidate will see them</h2>
                  <p className="muted" style={{ maxWidth: '62ch' }}>
                    Count the paragraphs against the page you copied from. If two have run
                    together, put a blank line between them in the text above and read it again.
                  </p>
                </div>

                {passages.map((passage) => (
                  <div key={passage.part} className="stack" style={{ gap: 'var(--s-2)' }}>
                    <div className="library__head">
                      <h3 className="library__title">
                        {passage.title || `Passage ${passage.part}`}
                      </h3>
                      <p className="muted">
                        <span className="mono">{passage.paragraphs}</span>{' '}
                        {passage.paragraphs === 1 ? 'paragraph' : 'paragraphs'}
                      </p>
                    </div>
                    <div
                      className="exam preview-passage"
                      data-scheme="bw"
                      data-size="regular"
                    >
                      <div
                        className="passage"
                        dangerouslySetInnerHTML={{ __html: passage.html }}
                      />
                    </div>
                  </div>
                ))}
              </section>
            )}

            {summary && (
              <section className="panel stack">
                <div>
                  <h2 className="panel__title">What it read</h2>
                  <p className="muted" style={{ maxWidth: '62ch' }}>
                    Each line is a guess made from an instruction in your text. Check the question
                    types before importing — a wrong one changes how the answers are marked.
                  </p>
                </div>
                <div className="ledger__wrap">
                  <table className="ledger">
                    <thead>
                      <tr>
                        <th className="num">Part</th>
                        <th>Questions</th>
                        <th>Read as</th>
                        <th>Word limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((row) => (
                        <tr key={`${row.part}-${row.range}`}>
                          <td className="num">{row.part || '—'}</td>
                          <td className="mono">{row.range}</td>
                          <td>{row.label ?? row.type.replace(/_/g, ' ').toLowerCase()}</td>
                          <td className="muted">{row.maxWords ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <div className="row">
              <button
                className="btn btn--primary"
                disabled={busy || template.trim().length < 20}
                onClick={() => void importIt()}
              >
                {busy ? 'Reading it…' : 'Import'}
              </button>
              {template.trim().length < 20 && (
                <span className="muted">Paste the paper above first.</span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
